/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { env } from "./cfg/env";
import getLang from "./util/langExtracter";

// lang variable
const lang = getLang();
let host = window.location.host;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
  names: {
    signUpSignIn: `${env.sso.policy.signIn}`,
    forgotPassword: `${env.sso.policy.forgotPassword}`,
    editProfile: `${env.sso.policy.editProfile}`,
    editSecurityQuestion: `${env.sso.policy.setSecurityQuestion}`,
    resetPassword: `${env.sso.policy.resetPassword}`,
  },
  authorities: {
    signUpSignIn: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.signIn}`,
    },
    forgotPassword: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.forgotPassword}`,
    },
    editProfile: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.editProfile}`,
    },
    editSecurityQuestion: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.setSecurityQuestion}`,
    },
    editPassword: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.resetPassword}`,
    },
    editSelfServeSecQuestion: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.selfServeSetSecurityQuestion}`,
    },
    activateTileMd2: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.activateTileMd2}`,
    },
    activateTileMgo: {
      authority: `https://${env.sso.b2cTenantUrl}/${env.sso.b2cTenantDomain}.onmicrosoft.com/${env.sso.policy.activateTileMgo}`,
    },
  },
  authorityDomain: `${env.sso.b2cTenantUrl}`,
};

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: `${env.sso.appClientId}`, // This is the ONLY mandatory field that you need to supply.
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: /local/.test(host)
      ? `http://localhost:3000/${lang}/home`
      : `https://${env.sso.webApp}/${lang}/home`, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: /local/.test(host)
      ? `http://localhost:3000/${lang}/home`
      : `https://${env.sso.webApp}/${lang}/home`, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiHello: {
    endpoint: "http://localhost:5000/hello",
    scopes: [`https://${env.sso.b2cTenantDomain}.onmicrosoft.com/sso/auth`], // e.g. api://xxxxxx/access_as_user
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [...protectedResources.apiHello.scopes],
};
